import React, { useCallback, useEffect, useState } from "react";
import { useHookstate } from "@hookstate/core";
import { useLang } from "~/hooks/useLang";
import type { IMessageOther, IOthersDTO } from "../../interfaces/IDispatchRegistrationOthers";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListRegistrations from "../DispatchRegistrationTab/components/ListRegistrations";
import useDispatchRegistrationState from "../../stores/DispatchRegistrationState";
import FormOthers from "./components/FormOthers";
import notify from '~/utils/notify';
import useDispatchRegistrationOthersState, { othersDefaultState } from "../../stores/DispatchRegistrationOthersState";
import { saveLogVisualizeQualificators } from "../../services";
import useDispatchState from "../../stores/DispatchState";
import { toDomain } from "~/mappers/registration-other-mappers";
import useSocket from "~/hooks/useSocket";
import useSystemState from "~/features/System/stores/SystemState";


interface IOthers {
  readonly handleClickOpenConfirmAction: (actionName: string) => void;
}

// eslint-disable-next-line max-lines-per-function
export default function Others({ handleClickOpenConfirmAction }: IOthers) {
  const { translate } = useLang();
  const { socket } = useSocket();
  const { windowManager } = useSystemState();
  const { registrationDispatch } = useDispatchRegistrationState();
  const { addRegistration, loadingRegistrations, registrationRowId, deleteRegistration } = registrationDispatch();
  const { registrationOtherDispatch, deleteOtherRecord, editOtherRecord, getAllAssociatedOtherRecord } = useDispatchRegistrationOthersState();
  const { other } = registrationOtherDispatch()
  const { dispatch } = useDispatchState();
  const { occurrence, id: dispatchIdState, status } = dispatch();
  const { id: occurrenceIdState, } = occurrence;
  const loading = useHookstate<boolean>(false)

  const [rows, setRows] = useState<readonly IOthersDTO[]>([])



  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: translate('Id'),
      flex: 0.5,
      valueFormatter: (row) => {
        return row ? row['name'] : '---';
      }
    },
    {
      field: 'item',
      headerName: translate('Item'),
      flex: 1,
      valueFormatter: (row) => {
        return row ? row['item'] : '---';
      }
    },
    {
      field: 'notes',
      headerName: translate('Note'),
      flex: 1,
      valueFormatter: (row) => {
        return row ? row['name'] : '---';
      }
    },
    {
      field: 'actions', type: 'actions', flex: 1,
      getActions: (params) => [
        status.value.id === 8 ? <Tooltip key={2} title={translate('View Record')}>
          <span>
            <GridActionsCellItem
              key={params.id}
              icon={<RemoveRedEyeIcon />}
              onClick={() => {
                saveLogVisualizeQualificators(params.row.id, '/occurrence/associated-other-record/generate-view-timeline').then((response) => {
                  if (response.status === 200) {
                    other.set(params.row);
                    addRegistration.set(true)
                  }
                })
              }}
              label="Print" />
          </span>
        </Tooltip> : <></>,
        status.value.id !== 8 ? <Tooltip key={2} title={translate('Edit Record')}>
          <span>
            <GridActionsCellItem
              key={params.id}
              icon={<EditIcon />}
              onClick={() => {
                other.set(params.row);
                addRegistration.set(true)
              }}
              label="Print" />
          </span>
        </Tooltip> : <></>,
        status.value.id !== 8 ? <Tooltip key={2} title={translate('Delete Record')}>
          <span>
            <GridActionsCellItem
              key={params.id}
              icon={<DeleteIcon />}
              onClick={() => {
                registrationRowId.set(params.row.id);
                handleClickOpenConfirmAction('delete')
              }}
              label="Print" />
          </span>
        </Tooltip> : <></>
      ]
    },
  ]

  const handleAdd = useCallback(() => {
    other.set({ ...othersDefaultState, occurrenceId: occurrenceIdState.get() });
    addRegistration.set(true)
  }, [addRegistration, occurrenceIdState, other])

  const handleDelete = useCallback(async (id: string) => {
    loadingRegistrations.set(true)
    await deleteOtherRecord(id)
    notify({ message: translate('Successfully Deleted'), type: 'success' })
    loadingRegistrations.set(false);
    deleteRegistration.set(false);
  }, [deleteOtherRecord, deleteRegistration, loadingRegistrations, translate])

  const handleEdit = useCallback(async () => {
    loading.set(true)
    await editOtherRecord(other.get()).finally(() => {
      loading.set(false)
    })
    notify({ message: translate('Saved successfully'), type: 'success' })
    addRegistration.set(false)
  }, [addRegistration, editOtherRecord, other, translate])

  useEffect(() => {
    if (windowManager()['registration'].open.get() && occurrenceIdState?.get()) {
      loadingRegistrations.set(true);
      getAllAssociatedOtherRecord(occurrenceIdState.get())
        .then((res) => {
          setRows(res)
          other.set(res)
        })
        .finally(() => {
          loadingRegistrations.set(false)
        });
    }
  }, [occurrenceIdState?.get()]);

  useEffect(() => {
    if (deleteRegistration.get()) {
      handleDelete(registrationRowId.get())
    }
  }, [deleteRegistration, handleDelete, registrationRowId])

  const newMessage = useCallback((message: IMessageOther) => {
    if (message.event_type === 'OCCURRENCE_ASSOCIATED_OTHER_RECORD_CREATE') {
      setTimeout(() => {
        if (message.content.dispatch_id !== dispatchIdState.get()) {
          return
        }
        setRows(prev => [...prev, toDomain(message.content)])
      }, 1000)
    }
    if (message.event_type === 'OCCURRENCE_ASSOCIATED_OTHER_RECORD_DELETE') {
      const filterRows = rows.filter(row => row.id !== message.content.id)
      setRows(filterRows);
    }
    if (message.event_type === 'OCCURRENCE_ASSOCIATED_OTHER_RECORD_CHANGE') {
      const updateRows = rows.map(row => {
        if (row.id === message.content.id) {
          return toDomain(message.content)
        } else {
          return row
        }
      })
      setRows(updateRows);
    }
  }, [rows])

  useEffect(() => {
    socket.on('occurrences', newMessage)
    return () => {
      socket.off('occurrences', newMessage)
    }
  }, [newMessage, socket])


  return (
    addRegistration.get() ? (
      <FormOthers
        loading={loading}
        handleEdit={handleEdit}
        handleClickOpenConfirmAction={handleClickOpenConfirmAction}
      />
    ) : (
      <ListRegistrations
        handleAdd={handleAdd}
        columns={columns}
        rows={rows}
      />
    )
  )
}


